import { useState } from 'react';

const team = [
  {
    name: 'Stavros Constantinides',
    role: 'Director',
    email: 'stavros.constantinides@creightoninvest.com',
    image: 'https://storage.readdy-site.link/project_files/cd9486cf-2809-47e9-86b7-caa7ed7f1efa/9ff16c8f-8952-4373-a06b-6da20e398ffe_compressed_leo.webp',
    impact: 'Founder and director of Creighton Investments Ltd since incorporation in June 2016. With decades of international business experience, Stavros brings strategic vision and disciplined governance to the firm\'s investment philosophy. A Cypriot national based in London, he oversees the long-term investment strategy and chairs all major portfolio decisions.',
  },
  {
    name: 'Eleni Constantinidi',
    role: 'Director',
    email: 'eleni.constantinidi@creightoninvest.com',
    image: 'https://public.readdy.ai/ai/img_res/edited_0f8af98c21e0c42db103efadbdd3d25b_2f2cbed3.jpg',
    impact: 'Appointed to the board in March 2026, Eleni brings fresh perspective and operational acumen to the firm\'s leadership. She plays a key role in portfolio monitoring, compliance oversight, and strategic planning — ensuring the company maintains the highest standards of UK corporate governance.',
  },
  {
    name: 'Arpad Habony',
    role: 'Chief Investment Officer',
    email: 'arpad.habony@creightoninvest.com',
    image: 'https://storage.readdy-site.link/project_files/cd9486cf-2809-47e9-86b7-caa7ed7f1efa/d00ab4d2-bbbf-4a00-a625-317c77471313_compressed_arpad.webp',
    impact: 'Leads all investment strategy, deal sourcing, and portfolio management. A former managing director at a top-tier private bank, Habony brings two decades of multi-asset allocation expertise. Under his leadership, the portfolio has grown to €2.4B while maintaining consistent double-digit returns across market cycles.',
  },
  {
    name: 'Dr. Alexander Stone',
    role: 'Chief Executive Officer',
    email: 'alexander@creightoninvest.com',
    impact: 'Drives the firm\'s global expansion strategy, institutional partnerships, and operational excellence. With a PhD in Economics from the London School of Economics and 18 years in investment management, Alexander has been instrumental in expanding the firm\'s footprint across international markets from its London base.',
  },
  {
    name: 'James Harrington',
    role: 'Head of Crypto & Digital Assets',
    email: 'james.harrington@creightoninvest.com',
    impact: 'Pioneers the firm\'s crypto-friendly investment strategy, managing €180M in digital asset allocations. James combines deep blockchain engineering knowledge with institutional risk management — a rare combination that has delivered 20.3% average annual returns in the crypto portfolio.',
  },
  {
    name: 'Adam Paz',
    role: 'Head of Investor Relations',
    email: 'adam@creightoninvest.com',
    impact: 'The primary point of contact for all institutional and professional investors. Adam manages relationships with pension funds, family offices, and sovereign wealth funds across Europe and the Middle East. His transparent communication style and deep product knowledge ensure every investor understands their exposure.',
  },
  {
    name: 'Margaret Walsh',
    role: 'Executive Director of Portfolio Operations',
    email: 'margaret.walsh@creightoninvest.com',
    impact: 'Oversees the day-to-day management of all active investments across five sectors. Margaret\'s operational discipline ensures that every asset receives active monitoring, performance optimization, and rigorous reporting. Her team is the engine that keeps the portfolio running at peak efficiency.',
  },
  {
    name: 'Dr. Richard Pembroke',
    role: 'Head of Real Estate Investments',
    email: 'richard.pembroke@creightoninvest.com',
    impact: 'Leads the firm\'s largest sector allocation at 35% of portfolio value. Richard oversees premium commercial real estate assets across 12 countries — from London Grade-A offices to international gateway city properties. With 25 years in institutional real estate investment, he has sourced and structured every major property acquisition.',
  },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    interest: '',
    range: '',
    message: '',
    accredited: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector('[name="phone_alt"]') as HTMLInputElement;
    if (honeypot && honeypot.value.trim() !== '') {
      setSubmitted(true);
      return;
    }
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        urlEncoded.append(key, value.toString());
      }
    });
    try {
      await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
    } catch (_) {
      // proceed to success message regardless
    }
    setSubmitted(true);
  };

  const faqs = [
    { q: 'What is the minimum investment?', a: 'Our minimum commitment starts at €100,000 for accredited professional investors and €500,000 for institutional partners. Please contact our investor relations team to discuss eligibility.' },
    { q: 'How are returns distributed?', a: 'Returns vary by investment vehicle and structure. Generally, distributions are made quarterly or semi-annually based on fund performance and the specific terms of each investment mandate.' },
    { q: 'What due diligence do you provide?', a: 'Full transparency: quarterly performance reports, annual audits by leading firms, and access to underlying asset data through our secure investor portal. UK corporate governance ensures rigorous oversight.' },
    { q: 'Can non-UK investors participate?', a: 'Yes. We accept institutional and accredited professional investors from most jurisdictions, subject to local regulatory requirements and KYC/AML verification.' },
    { q: 'How do I get started?', a: 'Submit the inquiry form below or email our investor relations team directly. We typically respond within 24 hours to schedule an introductory conversation.' },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20modern%20London%20corporate%20office%20interior%20with%20warm%20natural%20daylight%20streaming%20through%20large%20windows%2C%20view%20of%20green%20London%20residential%20mews%20outside%2C%20clean%20minimalist%20British%20design%2C%20professional%20meeting%20room%2C%20premium%20atmosphere%2C%20bright%20and%20airy%2C%20sophisticated%20UK%20investment%20firm&width=1920&height=700&seq=contact-hero-london-2026&orientation=landscape"
            alt="Creighton Investments Ltd – Contact Us"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background-50" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-300 font-semibold">
              Get in Touch
            </span>
            <h1 className="text-5xl md:text-7xl font-heading font-bold text-white mt-4 mb-6 tracking-tight">
              Contact Us
            </h1>
            <p className="text-lg text-white/80 leading-relaxed max-w-2xl">
              We welcome inquiries from institutional investors, family offices, sovereign wealth funds, and accredited professionals. Tell us about your investment interests and our team will respond within 24 hours from our London headquarters.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="relative py-16 md:py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-background-50 rounded-2xl p-12 text-center border border-foreground-100/20">
                  <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-3xl text-primary-600" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-foreground-950 mb-3">Thanks!</h3>
                  <p className="text-foreground-500 text-base max-w-md mx-auto">
                    Our team will contact you. Also you can use our email <a href="mailto:info@creightoninvest.com" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">info@creightoninvest.com</a>
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  action="https://readdy.ai/api/form/d8thjdhd9m2hsn3jsgtg"
                  method="POST"
                  data-readdy-form
                  className="bg-background-50 rounded-2xl p-8 md:p-10 border border-foreground-100/20"
                >
                  <h3 className="text-xl font-heading font-bold text-foreground-950 mb-2">
                    Investor Inquiry Form
                  </h3>
                  <p className="text-foreground-500 text-sm mb-8">
                    All fields marked with * are required.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="+41 XX XXX XX XX"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Organization
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="Company or institution"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Investment Interest *
                      </label>
                      <select
                        name="interest"
                        required
                        value={formData.interest}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option value="real-estate">Real Estate</option>
                        <option value="renewable-energy">Renewable Energy</option>
                        <option value="oil-gas">Oil &amp; Gas</option>
                        <option value="crypto">Crypto &amp; Digital Assets</option>
                        <option value="medtech">Medical Equipment</option>
                        <option value="multiple">Multiple Sectors</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                        Investment Range
                      </label>
                      <select
                        name="range"
                        value={formData.range}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option value="100k-500k">€100K – €500K</option>
                        <option value="500k-2m">€500K – €2M</option>
                        <option value="2m-10m">€2M – €10M</option>
                        <option value="10m-plus">€10M+</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-[2px] text-foreground-600 font-semibold mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-white border border-foreground-200/40 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="Tell us about your investment objectives and interests..."
                    />
                    <p className="text-foreground-300 text-xs mt-1.5">{formData.message.length}/500 characters</p>
                  </div>

                  <div className="mb-8">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="accredited"
                        required
                        checked={formData.accredited}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 rounded border-foreground-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                      />
                      <span className="text-foreground-500 text-sm leading-relaxed">
                        I confirm that I am an accredited or professional investor under applicable regulations and have read the disclaimer. *
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white text-sm uppercase tracking-[2px] font-semibold py-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Submit Inquiry
                  </button>

                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="field-alt" />
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-6">
              {/* Contact Details */}
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-foreground-100/20">
                <h4 className="text-[10px] uppercase tracking-[3px] text-foreground-400 mb-5">
                  Headquarters
                </h4>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  Creighton Investments Ltd<br />
                  12 Gateway Mews<br />
                  Bounds Green<br />
                  London, N11 2UT<br />
                  United Kingdom
                </p>
                <div className="flex flex-col gap-2 text-sm">
                  <a href="mailto:info@creightoninvest.com" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    info@creightoninvest.com
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t border-foreground-100/20">
                  <h4 className="text-[10px] uppercase tracking-[3px] text-foreground-400 mb-3">
                    Registration
                  </h4>
                  <p className="text-foreground-500 text-xs">Company No. 10256484</p>
                  <p className="text-foreground-500 text-xs">Incorporated 29 June 2016</p>
                  <p className="text-foreground-500 text-xs">SIC: 64209 — Holding Companies</p>
                </div>
              </div>

              {/* Why Partner */}
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-foreground-100/20">
                <h4 className="text-[10px] uppercase tracking-[3px] text-foreground-400 mb-5">
                  Why Partner With Us
                </h4>
                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-line-chart-line', label: '12.8% Avg Annual Return' },
                    { icon: 'ri-global-line', label: '18 Country Presence' },
                    { icon: 'ri-shield-check-line', label: 'UK Corporate Governance' },
                    { icon: 'ri-team-line', label: 'Dedicated IR Team' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-primary-600`} />
                      </div>
                      <span className="text-foreground-600 text-sm">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map & Location */}
      <section className="relative py-20 md:py-28 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Find Us
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 mb-5 tracking-tight">
              Find Our Headquarters
            </h2>
            <p className="text-foreground-500 text-base max-w-2xl mx-auto">
              Our headquarters are located at 12 Gateway Mews in Bounds Green, North London — a quiet residential mews with excellent transport connections to Central London and the City.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Address Card */}
            <div className="bg-white rounded-2xl border border-foreground-100/20 p-8 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-xl text-primary-600" />
                </div>
                <div>
                  <h3 className="text-sm font-heading font-bold text-foreground-950">Headquarters</h3>
                  <p className="text-foreground-400 text-xs">London, United Kingdom</p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <i className="ri-building-line text-foreground-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-foreground-700 text-sm leading-relaxed font-medium">Creighton Investments Ltd</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">12 Gateway Mews</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">Bounds Green</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">London, N11 2UT</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">United Kingdom</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-foreground-400 mt-0.5 shrink-0" />
                  <a href="mailto:info@creightoninvest.com" className="text-primary-600 hover:text-primary-700 text-sm transition-colors cursor-pointer">
                    info@creightoninvest.com
                  </a>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-car-line text-foreground-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      25 minutes to Central London<br />
                      40 minutes to City of London<br />
                      1 hour to Heathrow Airport
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map — takes 2 columns */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-foreground-100/20 overflow-hidden h-[400px] lg:h-auto min-h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.5341415925476!2d-0.13972268427931614!3d51.61640367964272!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761b9c8c8c8c8d%3A0x1a2b3c4d5e6f7a8b!2s12%20Gateway%20Mews%2C%20Bounds%20Green%2C%20London%20N11%202UT%2C%20UK!5e0!3m2!1sen!2suk!4v1690000000000!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Creighton Investments Ltd — 12 Gateway Mews, London"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="relative py-20 md:py-28 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              Our Team
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 mb-5 tracking-tight">
              Leadership &amp; Key Executives
            </h2>
            <p className="text-foreground-500 text-base max-w-2xl mx-auto">
              Every member of our leadership team brings decades of specialized expertise — from institutional asset management to blockchain infrastructure, from UK regulatory compliance to global investor relations. Together, they execute the vision that has defined our firm since 2016.
            </p>
          </div>

          {/* Board Members — First */}
          <div className="max-w-5xl mx-auto mb-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 bg-foreground-200/40" />
              <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold px-4">Board of Directors</span>
              <div className="h-px flex-1 bg-foreground-200/40" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-16">
            {team.slice(0, 3).map((person) => (
              <div key={person.email} className="bg-white rounded-2xl p-7 border border-foreground-100/20 hover:border-primary-200/40 transition-all duration-300 flex flex-col md:flex-row gap-5">
                <div className="shrink-0">
                  {person.image ? (
                    <img
                      src={person.image}
                      alt={person.name}
                      className="w-24 h-24 md:w-28 md:h-28 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl bg-background-100 border border-foreground-100/30 flex items-center justify-center">
                      <span className="text-xl md:text-2xl font-heading font-bold text-foreground-300">
                        {(() => { const parts = person.name.replace('Dr. ', '').split(' '); return parts[0][0] + parts[parts.length - 1][0]; })()}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-base font-heading font-bold text-foreground-950">{person.name}</h3>
                  <p className="text-primary-500 text-[10px] uppercase tracking-[1.5px] font-semibold mt-1 mb-3">{person.role}</p>
                  <p className="text-foreground-500 text-xs leading-relaxed mb-3">{person.impact}</p>
                  <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 text-xs transition-colors cursor-pointer">
                    <i className="ri-mail-line" />
                    {person.email}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Executive Team — Remaining 5 */}
          <div className="max-w-5xl mx-auto mb-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 bg-foreground-200/40" />
              <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold px-4">Executive Team</span>
              <div className="h-px flex-1 bg-foreground-200/40" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {team.slice(3).map((person) => (
              <div key={person.email} className="bg-white rounded-2xl p-6 border border-foreground-100/20 hover:border-primary-200/40 transition-all duration-300">
                {person.image ? (
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-20 h-20 rounded-xl object-cover mb-4"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-background-100 border border-foreground-100/30 flex items-center justify-center mb-4">
                    <span className="text-lg font-heading font-bold text-foreground-300">
                      {(() => { const parts = person.name.replace('Dr. ', '').split(' '); return parts[0][0] + parts[parts.length - 1][0]; })()}
                    </span>
                  </div>
                )}
                <h3 className="text-sm font-heading font-bold text-foreground-950">{person.name}</h3>
                <p className="text-primary-500 text-[10px] uppercase tracking-[1.5px] font-semibold mt-1 mb-3">{person.role}</p>
                <p className="text-foreground-500 text-xs leading-relaxed mb-3">{person.impact}</p>
                <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 text-xs transition-colors cursor-pointer">
                  <i className="ri-mail-line" />
                  {person.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[10px] uppercase tracking-[3px] text-primary-500 font-semibold">
              FAQ
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mt-4 tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto flex flex-col gap-3">
            {faqs.map((faq) => (
              <details key={faq.q} className="group bg-background-50 rounded-xl border border-foreground-100/20">
                <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                  <span className="text-foreground-900 font-medium text-sm pr-4">{faq.q}</span>
                  <i className="ri-add-line text-foreground-400 group-open:hidden shrink-0" />
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block shrink-0" />
                </summary>
                <div className="px-6 pb-5">
                  <p className="text-foreground-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
